/**
 * The drawing tool wheel's gesture, as a pure state machine.
 *
 *   closed  --press-->  held      Tab went down over a drawing surface.
 *   held    --release-> closed    Tab came up: picks the hot slot, or cancels
 *                                 when the pointer is still in the hub (which
 *                                 also turns off the armed tool).
 *   held    --click-->  closed    A tile clicked while Tab is held: picks it.
 *   held    --wrench--> editing   Pinned open to rearrange; release keeps it.
 *   editing --press / wrench--> closed
 *
 * `transitionWheel` returns the next state plus the slot to arm, if any, and
 * whether to disarm the armed tool; the host performs both, so this module
 * has no side effects.
 */
import type { WheelPoint } from '../utils/toolWheelGeometry'

export type WheelPhase = 'closed' | 'held' | 'editing'

export interface WheelState {
  phase: WheelPhase
  /** The hub's center, in viewport px. */
  origin: WheelPoint
  /** Viewport fit (1 = full size). */
  scale: number
  /** The slot under the pointer's direction, or null in the hub. */
  hovered: number | null
}

export type WheelEvent =
  | { type: 'press'; origin: WheelPoint; scale: number }
  | { type: 'release' }
  | { type: 'hover'; slot: number | null }
  | { type: 'click'; slot: number }
  | { type: 'toggleEdit' }
  | { type: 'blur' }
  | { type: 'dismiss' }

/**
 * How long a closed wheel stays on screen fading out (ms). It is inert and
 * lets clicks through the whole time, so the click that places a drawing
 * reaches the chart.
 */
export const WHEEL_EXIT_MS = 120

export const CLOSED_WHEEL: WheelState = {
  phase: 'closed',
  origin: { x: 0, y: 0 },
  scale: 1,
  hovered: null,
}

/**
 * What a transition asks the host to do: the next state, the slot to arm (if
 * any) and whether to turn off the surface's armed tool. `cancel` is set only
 * when Tab comes up in the hub, so tapping Tab disarms whatever tool was armed,
 * while a blur or closing edit mode leaves it alone.
 */
export interface WheelTransition {
  state: WheelState
  pick: number | null
  cancel: boolean
}

const close = (pick: number | null = null, cancel = false): WheelTransition => ({
  state: CLOSED_WHEEL,
  pick,
  cancel,
})

export function transitionWheel(state: WheelState, event: WheelEvent): WheelTransition {
  const stay: WheelTransition = { state, pick: null, cancel: false }
  const to = (next: WheelState): WheelTransition => ({ state: next, pick: null, cancel: false })
  switch (state.phase) {
    case 'closed':
      if (event.type !== 'press') return stay
      return to({ phase: 'held', origin: event.origin, scale: event.scale, hovered: null })

    case 'held':
      switch (event.type) {
        case 'release':
          // Released on a slot: arm it. Released in the hub: cancel, and turn
          // off the tool that was armed (tapping Tab again disarms it).
          return state.hovered === null ? close(null, true) : close(state.hovered)
        case 'click':
          return close(event.slot)
        case 'hover':
          return to({ ...state, hovered: event.slot })
        case 'toggleEdit':
          return to({ ...state, phase: 'editing', hovered: null })
        case 'blur':
        case 'dismiss':
          return close()
        case 'press':
          return stay
      }
      return stay

    case 'editing':
      switch (event.type) {
        case 'press':
        case 'toggleEdit':
        case 'dismiss':
          return close()
        case 'hover':
          return to({ ...state, hovered: event.slot })
        case 'release':
        case 'click':
        case 'blur':
          return stay
      }
      return stay
  }
}
